import { Component } from '@theme/component';
import { ThemeEvents, SlideshowSelectEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/** @type {WeakMap<HTMLTemplateElement, Promise<HTMLImageElement | Element | null>>} */
const deferredImageLoads = new WeakMap();

/**
 * Hydrates responsive product images that were kept inert in a template.
 *
 * @param {Element} root - Container that owns one or more deferred product images.
 * @returns {Promise<(HTMLImageElement | Element | null)[]>}
 */
export function hydrateDeferredProductImages(root) {
  if (!(root instanceof Element)) return Promise.resolve([]);

  const templates = Array.from(root.querySelectorAll('template[data-product-media-image-template]'));
  return Promise.all(templates.map(hydrateDeferredProductImage));
}

/**
 * Starts one deferred image request while its placeholder remains visible.
 *
 * @param {HTMLTemplateElement} template
 * @returns {Promise<HTMLImageElement | Element | null>}
 */
function hydrateDeferredProductImage(template) {
  const pending = deferredImageLoads.get(template);
  if (pending) return pending;

  const source = template.content.querySelector('img.product-media__image');
  if (!(source instanceof HTMLImageElement)) return Promise.resolve(null);

  const image = /** @type {HTMLImageElement} */ (source.cloneNode(true));
  const container = template.closest('.product-media');
  const placeholder = container?.querySelector('.product-media__deferred-placeholder');

  image.loading = 'eager';
  image.classList.add('product-media__image--hydrating');

  const promise = new Promise((resolve) => {
    let settled = false;

    const reveal = async () => {
      if (settled) return;
      settled = true;

      try {
        await image.decode();
      } catch {
        // A successful load is enough when decode() is unavailable or rejected.
      }

      image.classList.remove('product-media__image--hydrating');
      placeholder?.remove();
      template.remove();
      resolve(image);
    };

    const fail = () => {
      if (settled) return;
      settled = true;
      image.remove();
      deferredImageLoads.delete(template);
      resolve(placeholder ?? null);
    };

    image.addEventListener('load', reveal, { once: true });
    image.addEventListener('error', fail, { once: true });
    template.before(image);

    if (image.complete) {
      queueMicrotask(() => (image.naturalWidth > 0 ? reveal() : fail()));
    }
  });

  deferredImageLoads.set(template, promise);
  return promise;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (!this.querySelector('template[data-product-media-image-template]')) return;

    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.addEventListener('pointerdown', this.#handleGalleryIntent, { capture: true, passive: true, signal });
    this.addEventListener('focusin', this.#handleGalleryIntent, { capture: true, signal });
    this.addEventListener('click', this.#handleGalleryIntent, { capture: true, signal });

    const priorityImage = this.querySelector('img.product-media__image[fetchpriority="high"]');
    if (!(priorityImage instanceof HTMLImageElement) || priorityImage.complete) {
      this.#observeDeferredImages();
    } else {
      priorityImage.addEventListener('load', this.#observeDeferredImages, { once: true, signal });
      priorityImage.addEventListener('error', this.#observeDeferredImages, { once: true, signal });
    }
  }

  #controller = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #slideshowImageObserver;

  /** @type {IntersectionObserver | undefined} */
  #gridImageObserver;

  #observersArmed = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#slideshowImageObserver?.disconnect();
    this.#gridImageObserver?.disconnect();
  }

  /**
   * Arms viewport observers only after the priority image finishes, so deferred
   * gallery media cannot compete with the LCP request.
   */
  #observeDeferredImages = () => {
    if (this.#observersArmed || !this.isConnected) return;
    this.#observersArmed = true;

    const slideshowScroller = this.querySelector('slideshow-component[ref="slideshow"] slideshow-slides');
    const slideshowSlides = slideshowScroller?.querySelectorAll(':scope > slideshow-slide') ?? [];
    const gridItems = this.querySelectorAll('.media-gallery__grid > .product-media-container');

    if (!('IntersectionObserver' in window)) {
      for (const container of [...slideshowSlides, ...gridItems]) {
        hydrateDeferredProductImages(container);
      }
      return;
    }

    if (slideshowScroller instanceof HTMLElement) {
      this.#slideshowImageObserver = new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.01) continue;
            hydrateDeferredProductImages(entry.target);
            observer.unobserve(entry.target);
          }
        },
        { root: slideshowScroller, rootMargin: '0px 90% 0px 90%', threshold: 0.01 }
      );

      for (const slide of slideshowSlides) {
        if (slide.querySelector('template[data-product-media-image-template]')) {
          this.#slideshowImageObserver.observe(slide);
        }
      }
    }

    this.#gridImageObserver = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          hydrateDeferredProductImages(entry.target);
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '300px 0px', threshold: 0.01 }
    );

    for (const item of gridItems) {
      if (item.querySelector('template[data-product-media-image-template]')) {
        this.#gridImageObserver.observe(item);
      }
    }
  };

  /**
   * Starts loading the target of a dot/thumbnail/arrow before its click handler
   * scrolls the gallery. A drag gesture warms both adjacent slides.
   *
   * @param {Event} event
   */
  #handleGalleryIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const action = event.target.closest('button')?.getAttribute('on:click');
    const selectMatch = action?.match(/^\/select\/(\d+)$/);
    if (selectMatch) {
      this.#hydrateSlideshowIndex(Number(selectMatch[1]), true);
      return;
    }

    const current = this.slideshow?.current ?? 0;
    if (action === '/next') {
      this.#hydrateSlideshowIndex(current + 1, true);
      return;
    }
    if (action === '/previous') {
      this.#hydrateSlideshowIndex(current - 1);
      return;
    }

    if (event.type === 'pointerdown' && event.target.closest('slideshow-slides')) {
      this.#hydrateSlideshowIndex(current - 1);
      this.#hydrateSlideshowIndex(current + 1);
    }
  };

  /**
   * Hydrates the selected slide synchronously and then warms the next slide in
   * the user's travel direction after the selected image is decoded.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (event.target !== this.slideshow) return;

    const { index, previousIndex, slide, userInitiated } = event.detail;
    const selected = hydrateDeferredProductImages(slide);
    if (!userInitiated) return;

    selected.then(() => {
      const direction = index >= previousIndex ? 1 : -1;
      this.#hydrateSlideshowIndex(index + direction);
    });
  };

  /**
   * @param {number} index
   * @param {boolean} [hydrateNext=false]
   */
  #hydrateSlideshowIndex(index, hydrateNext = false) {
    const slides = this.slideshow?.slides;
    const slide = slides?.[index];
    if (!slide) return;

    hydrateDeferredProductImages(slide);
    if (hydrateNext) {
      const nextSlide = slides?.[index + 1];
      if (nextSlide) hydrateDeferredProductImages(nextSlide);
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // DOMParser treats <noscript> contents as active markup. Remove the no-JS
    // fallbacks before inserting section-rendered HTML into the live document.
    for (const fallback of newMediaGallery.querySelectorAll('noscript[data-product-media-noscript]')) {
      fallback.remove();
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
